import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getToken, slugifyFilename, fmtR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, Download, Ban } from "lucide-react";

export default function CouponDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [qrUrl, setQrUrl] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/admin/coupons/${id}`);
                setData(r.data);
            } catch (e) {
                toast.error("Failed to load coupon");
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!data) return;
        const token = getToken();
        fetch(`${api.defaults.baseURL}/admin/coupons/${id}/qr`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.blob())
            .then(b => setQrUrl(URL.createObjectURL(b)));
        return () => { if (qrUrl) URL.revokeObjectURL(qrUrl); };
    }, [data, id]);

    const downloadQr = () => {
        if (!qrUrl || !data) return;
        const a = document.createElement("a");
        a.href = qrUrl;
        a.download = `${slugifyFilename(data.coupon.name)}.png`;
        a.click();
    };

    const voidCoupon = async () => {
        if (!window.confirm("Void this coupon? This cannot be undone.")) return;
        try {
            await api.post(`/admin/coupons/${id}/void`);
            toast.success("Coupon voided");
            const r = await api.get(`/admin/coupons/${id}`);
            setData(r.data);
        } catch (e) {
            toast.error("Failed");
        }
    };

    if (!data) return <div className="p-8 text-[#7A726D]">Loading…</div>;
    const c = data.coupon;
    const redemptions = data.redemptions || [];

    return (
        <div className="p-8 max-w-5xl mx-auto" data-testid="coupon-detail-page">
            <Link to="/admin/coupons"
                className="inline-flex items-center text-sm text-[#7A726D] hover:text-bc-orange mb-4"
                data-testid="back-to-coupons">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to coupons
            </Link>

            <div className="flex items-start justify-between mb-6 gap-6">
                <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-[#7A726D]">Coupon</div>
                    <h1 className="font-display text-3xl font-bold text-espresso tracking-tight mt-1">{c.name}</h1>
                    <div className="mt-2 flex items-center gap-3 text-sm text-[#7A726D]">
                        <code className="font-mono text-[#3E2723]">{c.code}</code>
                        <StatusBadge status={c.status} />
                    </div>
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button variant="outline" onClick={downloadQr} data-testid="download-qr-btn">
                        <Download className="w-4 h-4 mr-2" /> QR
                    </Button>
                    {c.status === "active" && (
                        <Button variant="outline" onClick={voidCoupon}
                            className="text-[#B24A45] border-[#B24A45]/30 hover:bg-[#B24A45]/5"
                            data-testid="void-coupon-btn">
                            <Ban className="w-4 h-4 mr-2" /> Void
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mb-6">
                <Card className="col-span-12 md:col-span-8 border-[#E5E0D8] shadow-none rounded-sm">
                    <CardContent className="p-5 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                        <Info label="Discount">
                            {c.discount_type === "percent" ? `${c.discount_value}% off` : `R${c.discount_value} off`}
                        </Info>
                        <Info label="Uses">{c.uses_remaining}/{c.max_uses}</Info>
                        <Info label="Expiry">{c.expiry_date ? c.expiry_date.slice(0, 10) : "—"}</Info>
                        <Info label="Created">{c.created_at?.slice(0, 10) || "—"}</Info>
                        <Info label="Cost shop">{c.cost_shop_id ? c.cost_shop_id : "—"}</Info>
                        <Info label="Allowed branches">
                            {c.allowed_branch_ids?.length ? `${c.allowed_branch_ids.length} selected` : "All"}
                        </Info>
                        {c.restrictions && (
                            <div className="col-span-2">
                                <div className="text-xs uppercase text-[#7A726D]">Restrictions</div>
                                <div className="text-[#3E2723]">{c.restrictions}</div>
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card className="col-span-12 md:col-span-4 border-[#E5E0D8] shadow-none rounded-sm">
                    <CardContent className="p-5 flex items-center justify-center">
                        {qrUrl ? (
                            <img src={qrUrl} alt={`${c.name} QR`} className="w-44 h-44" data-testid="coupon-qr-img" />
                        ) : (
                            <div className="text-xs text-[#7A726D]">Generating QR…</div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <h2 className="font-display text-xl font-bold text-espresso mb-3">Redemption history</h2>
            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="redemptions-table">
                        <TableHeader>
                            <TableRow className="bg-[#F9F8F6]">
                                <TableHead>Date</TableHead>
                                <TableHead>Branch</TableHead>
                                <TableHead>Receipt #</TableHead>
                                <TableHead>Discount applied</TableHead>
                                <TableHead>Source</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {redemptions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-[#7A726D] py-8">
                                        No redemptions yet
                                    </TableCell>
                                </TableRow>
                            )}
                            {redemptions.map(r => (
                                <TableRow key={r.id} data-testid={`redemption-row-${r.id}`}>
                                    <TableCell className="text-xs">{r.redeemed_at?.slice(0, 19).replace("T", " ")}</TableCell>
                                    <TableCell className="text-sm">{r.branch_name || r.branch_id || "—"}</TableCell>
                                    <TableCell className="text-sm font-mono">{r.receipt_number || "—"}</TableCell>
                                    <TableCell className="text-sm">
                                        {r.amount_redeemed ? fmtR(r.amount_redeemed) : (r.discount_value ? `${r.discount_value}${c.discount_type === "percent" ? "%" : ""}` : "—")}
                                    </TableCell>
                                    <TableCell className="text-xs text-[#7A726D]">{r.source || "scanner"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}

function Info({ label, children }) {
    return (
        <div>
            <div className="text-xs uppercase text-[#7A726D]">{label}</div>
            <div className="text-[#3E2723] mt-0.5">{children}</div>
        </div>
    );
}
